/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useRef, useState, useEffect } from "react";
import "./App.scss";
import { LiveAPIProvider } from "./contexts/LiveAPIContext";
import SidePanel from "./components/side-panel/SidePanel";
import { ToolsManager } from "./components/tools-manager/ToolsManager";
import { Altair } from "./components/altair/Altair";
import { EmailSender } from "./components/email-sender/EmailSender";
import { CapabilitiesCard } from "./components/capabilities-card/CapabilitiesCard";
import ControlTray from "./components/control-tray/ControlTray";
import Login from "./components/login/Login";
import cn from "classnames";
import { LiveClientOptions } from "./types";
import Plasma from "./components/plasma/Plasma";

const API_KEY = process.env.REACT_APP_GEMINI_API_KEY as string;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL as string;
const PROJECT_ID = process.env.REACT_APP_GOOGLE_CLOUD_PROJECT as string;
const LOCATION = process.env.REACT_APP_GOOGLE_CLOUD_LOCATION as string;
// Default to Vertex AI if backend URL is present, otherwise check explicit flag
const USE_VERTEX_AI = BACKEND_URL ? true : (process.env.REACT_APP_USE_VERTEX_AI === 'true');

console.log('App initialization:', {
  USE_VERTEX_AI,
  BACKEND_URL,
  hasApiKey: !!API_KEY,
  PROJECT_ID,
  LOCATION
});

function createApiOptions(): LiveClientOptions {
  if (USE_VERTEX_AI && BACKEND_URL) {
    // Using backend proxy for Vertex AI - no credentials needed in browser
    console.log('✅ Using Vertex AI with backend proxy:', BACKEND_URL);
    return {};
  } else if (USE_VERTEX_AI) {
    // Direct Vertex AI (won't work in browser, but keep for compatibility)
    console.warn('⚠️ Vertex AI enabled but no BACKEND_URL found');
    if (!PROJECT_ID || !LOCATION) {
      throw new Error(
        "When using Vertex AI, set REACT_APP_GOOGLE_CLOUD_PROJECT and REACT_APP_GOOGLE_CLOUD_LOCATION in .env"
      );
    }

    return {
      vertexai: true,
      project: PROJECT_ID,
      location: LOCATION,
    };
  } else {
    console.log('Using Gemini API with API key');
    if (!API_KEY) {
      throw new Error("set REACT_APP_GEMINI_API_KEY in .env");
    }

    return {
      apiKey: API_KEY,
    };
  }
}

const apiOptions: LiveClientOptions = createApiOptions();

type AuthStage = "login" | "transition-in" | "app";
const TRANSITION_DURATION_MS = 900;

function AppContent({ onLogout }: { onLogout: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  return (
    <div className="app-shell">
      <div className="app-plasma-layer">
        <Plasma
          color="#448dff"
          speed={0.6}
          direction="forward"
          scale={1.1}
          opacity={0.8}
          mouseInteractive
          className="app-plasma"
        />
        <div className="app-plasma-overlay" />
      </div>
      <ToolsManager />
      <div className="streaming-console">
        <SidePanel onOpenChange={setSidebarOpen} />
        <main>
          <header className={cn("app-header", { "sidebar-open": sidebarOpen })}>
            <div className="branding">
              <img src="/hapticware-logo.svg" alt="Hapticware" className="logo" />
              <div className="brand-text">
                <h1>Trezr</h1>
                <span className="subtitle">Multimodal Agent by Hapticware</span>
              </div>
            </div>
            <button className="logout-button" onClick={onLogout}>
              <span className="material-symbols-outlined">logout</span>
              Logout
            </button>
          </header>
          <div className="main-app-area">
            <CapabilitiesCard />
            <Altair />
            <EmailSender />
            <video
              className={cn("stream", {
                hidden: !videoRef.current || !videoStream,
              })}
              ref={videoRef}
              autoPlay
              playsInline
            />
          </div>

          <ControlTray
            videoRef={videoRef}
            supportsVideo={true}
            onVideoStreamChange={setVideoStream}
            enableEditingSettings={true}
          >
            {/* put your own buttons here */}
          </ControlTray>
        </main>
      </div>
    </div>
  );
}

function App() {
  const [stage, setStage] = useState<AuthStage>("login");
  const transitionTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const authStatus = sessionStorage.getItem("isAuthenticated");
    if (authStatus === "true") {
      setStage("app");
    }
  }, []);

  useEffect(() => {
    return () => {
      if (transitionTimerRef.current) {
        window.clearTimeout(transitionTimerRef.current);
        transitionTimerRef.current = null;
      }
    };
  }, []);

  const startTransitionToApp = () => {
    if (stage !== "login") {
      return;
    }

    sessionStorage.setItem("isAuthenticated", "true");
    setStage("transition-in");

    if (transitionTimerRef.current) {
      window.clearTimeout(transitionTimerRef.current);
    }

    transitionTimerRef.current = window.setTimeout(() => {
      setStage("app");
      transitionTimerRef.current = null;
    }, TRANSITION_DURATION_MS);
  };

  const handleLogin = () => {
    startTransitionToApp();
  };

  const handleLogout = () => {
    sessionStorage.removeItem("isAuthenticated");
    if (transitionTimerRef.current) {
      window.clearTimeout(transitionTimerRef.current);
      transitionTimerRef.current = null;
    }
    setStage("login");
  };

  return (
    <div className="App">
      <div className="app-root">
        {stage !== "app" && (
          <div
            className={cn("stage", "login-stage", {
              "is-active": stage === "login",
              "is-transitioning": stage === "transition-in",
            })}
          >
            <Login onLogin={handleLogin} />
          </div>
        )}

        {stage !== "login" && (
          <div
            className={cn("stage", "app-stage", {
              "is-entering": stage === "transition-in",
              "is-active": stage === "app",
            })}
          >
            <LiveAPIProvider options={apiOptions}>
              <AppContent onLogout={handleLogout} />
            </LiveAPIProvider>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
